// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 MindTenet LLC
// This file is part of Undef Engine.

/**
 * Re-export of the main TreatmentCard with base-specific adapter.
 *
 * This file exists for backward compatibility with CardHand and SubmissionArea.
 * New components should import TreatmentCard directly from '../../TreatmentCard'.
 */

import { memo } from 'react';
import { TreatmentCard as MainTreatmentCard } from '../../TreatmentCard';
import type { TreatmentCard as TreatmentCardType } from '../../../types/game';

// Re-export the Card type from the local types for backward compatibility
// with CardHand and SubmissionArea which use the simpler Card interface
export type { Card } from './types';

interface TreatmentCardProps {
  card: {
    id: string;
    text: string;
    image_url?: string | null;
  };
  selected?: boolean;
  onSelect?: (cardId: string) => void;
  disabled?: boolean;
  className?: string;
}

/**
 * Adapter component that wraps the main TreatmentCard for base game components.
 *
 * Maps the simplified Card type and onSelect callback to the main component's API.
 */
export const TreatmentCard = memo(function TreatmentCard({
  card,
  selected = false,
  onSelect,
  disabled = false,
  className = '',
}: TreatmentCardProps) {
  // Convert the simplified card type to the full TreatmentCard type
  const fullCard: TreatmentCardType = {
    id: card.id,
    text: card.text,
    card_type: 'TreatmentCard',
  };

  return (
    <MainTreatmentCard
      card={fullCard}
      selected={selected}
      selectable={!disabled && !!onSelect}
      onClick={onSelect ? () => onSelect(card.id) : undefined}
      className={className}
    />
  );
});

export type { TreatmentCardProps };
